const CLIENT_ID = "store";
const PROTOCOL_VERSION = 2;
const ACCOUNTS_URL = process.env.REACT_APP_IDENTITY_URL || "https://accounts.clikkle.com";
const PRODUCTION_REDIRECT = "https://store.clikkle.com/";
const INTENDED_ROUTE_KEY = "clikkle.identity.store.intendedRoute";
const COMMERCE_ROUTE_KEY = "clikkle.identity.store.commerceRoute";
const AUTH_CALLBACK_KEYS = new Set(["userId", "refreshToken", "accessToken", "sessionToken", "securityLevel"]);

const getRedirectUri = () => {
  if (typeof window === "undefined") return PRODUCTION_REDIRECT;
  if (window.location.hostname === "localhost" || /^192\.168\./.test(window.location.hostname)) return `${window.location.origin}/`;
  return PRODUCTION_REDIRECT;
};

const sanitizeSearch = (search = "") => {
  const params = new URLSearchParams(search);
  AUTH_CALLBACK_KEYS.forEach((key) => params.delete(key));
  const value = params.toString();
  return value ? `?${value}` : "";
};

const getCommerceDestination = () => {
  if (typeof window === "undefined") return "";
  const search = sanitizeSearch(window.location.search);
  const params = new URLSearchParams(search);
  if (!params.get("planId")) return "";
  return `/setup-flow${search}${window.location.hash || ""}`;
};

export const getAuthenticatedStoreDestination = () => {
  const currentCommerce = getCommerceDestination();
  if (currentCommerce) {
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    localStorage.removeItem(COMMERCE_ROUTE_KEY);
    return currentCommerce;
  }
  return consumeStoredCommerceDestination() || "/myStore";
};

const consumeStoredCommerceDestination = () => {
  if (typeof window === "undefined") return "";
  const sessionValue = sessionStorage.getItem(INTENDED_ROUTE_KEY);
  const commerceValue = localStorage.getItem(COMMERCE_ROUTE_KEY);
  const value = sessionValue || commerceValue || "";
  if (!value.startsWith("/setup-flow")) return "";
  sessionStorage.removeItem(INTENDED_ROUTE_KEY);
  localStorage.removeItem(COMMERCE_ROUTE_KEY);
  return value;
};

export const getAuthSwitchPath = (mode = "login") => {
  const path = mode === "signup" ? "/signup" : "/login";
  if (typeof window === "undefined") return path;

  // Prefer the live commerce query. If a legacy/plain auth link already
  // dropped it, recover the saved /setup-flow transaction rather than
  // falling back to /myStore and losing the purchase.
  let search = sanitizeSearch(window.location.search);
  const liveParams = new URLSearchParams(search);

  if (!liveParams.get("planId")) {
    const stored =
      sessionStorage.getItem(INTENDED_ROUTE_KEY) ||
      localStorage.getItem(COMMERCE_ROUTE_KEY) ||
      "";

    if (stored.startsWith("/setup-flow")) {
      const queryIndex = stored.indexOf("?");
      const hashIndex = stored.indexOf("#");
      if (queryIndex >= 0) {
        const end = hashIndex > queryIndex ? hashIndex : stored.length;
        search = sanitizeSearch(stored.slice(queryIndex, end));
      }
    }
  }

  return `${path}${search}`;
};

export const saveIdentityDestination = () => {
  if (typeof window === "undefined") return;

  const commerceDestination = getCommerceDestination();
  if (commerceDestination) {
    sessionStorage.setItem(INTENDED_ROUTE_KEY, commerceDestination);
    localStorage.setItem(COMMERCE_ROUTE_KEY, commerceDestination);
    return;
  }

  const route = `${window.location.pathname}${sanitizeSearch(window.location.search)}${window.location.hash}`;
  if (!route.startsWith("/login") && !route.startsWith("/signup")) {
    sessionStorage.setItem(INTENDED_ROUTE_KEY, route);
  }
};

export const consumeIdentityDestination = (fallback = "/") => {
  const sessionValue = sessionStorage.getItem(INTENDED_ROUTE_KEY);
  const commerceValue = localStorage.getItem(COMMERCE_ROUTE_KEY);
  sessionStorage.removeItem(INTENDED_ROUTE_KEY);
  localStorage.removeItem(COMMERCE_ROUTE_KEY);
  const value = sessionValue || commerceValue;
  return value && value.startsWith("/") ? value : fallback;
};

export const buildIdentityUrl = (mode = "login") => {
  saveIdentityDestination();
  const path = mode === "signup" ? "/register" : "/login";
  const url = new URL(path, ACCOUNTS_URL);
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("identity_protocol_version", String(PROTOCOL_VERSION));
  url.searchParams.set("redirect_uri", getRedirectUri());
  url.searchParams.set("referralApp", "Clikkle Store");
  return url.toString();
};

export const beginIdentityLogin = () => { window.location.assign(buildIdentityUrl("login")); };
export const beginIdentityRegistration = () => { window.location.assign(buildIdentityUrl("signup")); };
export const IDENTITY_CLIENT = Object.freeze({ id: CLIENT_ID, protocolVersion: PROTOCOL_VERSION, redirectUri: getRedirectUri() });
